import { 
  Box, 
  Flex, 
  HStack, 
  Icon, 
  Text, 
  VStack, 
  Button, 
  Input,
} from "@chakra-ui/react";
import { generateTigaAnswer, useCommentaryContext } from "context/CommentaryContext";
import React, { useState, useEffect, useRef } from "react";
import { FaLightbulb } from "react-icons/fa";
import { FaPlus } from "react-icons/fa";
import InsightComponent from "../../components/analyse/InsightComponent";
import { StatefulRequestBuilder } from "../stateful/StatefulRequests";
import { statefulAnalysisTree } from "../../services/AnalysisTreeServices";
import { InsightsProto } from "build-ts/analysis";

interface InsightsPanelContainerProps {
  onToggleChat: () => void;
  isChatOpen: boolean;
}

const InsightsPanelContainer: React.FC<InsightsPanelContainerProps> = ({
  isChatOpen,
}) => {
  const { newInsights, setNewInsights, transactionId, setHasAnyLoading } = useCommentaryContext();
  const [newQuestion, setNewQuestion] = useState("");
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  // Map of loading states for questions keyed by question index.
  const [loadingStates, setLoadingStates] = useState<Map<number, boolean>>(new Map());
  // Reference to track if initial auto-generation has been done for this transaction
  const hasInitialAutoGenerated = useRef<boolean>(false);
  
  // Auto-generate missing answers only on initial load
  useEffect(() => {
    // Only auto-generate if we have insights and haven't done initial generation for this transaction
    if (newInsights && newInsights.length > 0 && !hasInitialAutoGenerated.current) {
      hasInitialAutoGenerated.current = true;
      generateMissingAnswers();
    }
  }, [newInsights, transactionId]);

  // Reset initial auto-generation flag when transaction changes (new session/tree)
  useEffect(() => {
    hasInitialAutoGenerated.current = false;
  }, [transactionId]);

  // Helper function to update loading state for a specific question.
  const updateLoadingState = (questionIndex: number, isLoading: boolean) => {
    setLoadingStates(prev => {
      const newLoadingStates = new Map(prev);
      newLoadingStates.set(questionIndex, isLoading);
      return newLoadingStates;
    });
  };

  // Check if a specific question is loading.
  const isQuestionLoading = (questionIndex: number): boolean => {
    return loadingStates.get(questionIndex) || false;
  };

  // Calculate overall loading progress for questions.
  const getLoadingProgress = (): { loading: number; total: number } => {
    let loading = 0;
    let total = 0;
    
    loadingStates.forEach((isLoading) => {
      total++;
      if (isLoading) loading++;
    });
    
    return { loading, total };
  };

  const { loading: loadingCount } = getLoadingProgress();
  const hasAnyLoading = loadingCount > 0;
  
  // Update the context loading state (used to disable save buttons)
  useEffect(() => {
    setHasAnyLoading(hasAnyLoading);
  }, [hasAnyLoading]);

  // Common function to update insights.
  const updateInsights = async (updatedInsights: InsightsProto[]) => {
    // Update local state
    setNewInsights(updatedInsights);
    
    // Send SetQnA request to persist changes
    const requestBuilder = new StatefulRequestBuilder(transactionId);
    const setQnARequest = requestBuilder.SetInsightsRequest(updatedInsights);
    await statefulAnalysisTree(requestBuilder.createStatefulRequest([setQnARequest]));
  };

  // Common function to add a question and generate its answer
  const addQuestionAndGenerateAnswer = async (question: string) => {
    const newQnA: InsightsProto = {
      question: question,
      answer: "" // Empty answer initially
    };
    
    const updatedInsights = [...(newInsights || []), newQnA];
    const questionIndex = updatedInsights.length - 1;
    
    // Always set the question.
    await updateInsights(updatedInsights);
    
    if (question.trim()) {
      updateLoadingState(questionIndex, true);
      const generatedAnswer = await generateTigaAnswer(question, transactionId);
      if (generatedAnswer) {
        // Update with the answer when it comes back
        const updatedInsightsWithAnswer = [...updatedInsights];
        updatedInsightsWithAnswer[questionIndex] = {
          ...updatedInsightsWithAnswer[questionIndex],
          answer: generatedAnswer
        };
        await updateInsights(updatedInsightsWithAnswer);
      }
      // Question stays even if answer generation fails or returns empty
      updateLoadingState(questionIndex, false);
    }
    
    return questionIndex;
  };
  
  // Generate missing answers for all insights on initial load.
  const generateMissingAnswers = async () => {
    if (!newInsights || newInsights.length === 0) return;
    // Collect all questions that need answers.
    const questionsToGenerate: Array<{
      questionIndex: number;
      question: string;
    }> = [];
    // Iterate over all insights and collect all questions that need answers.
    newInsights?.forEach((qna, questionIndex) => {
      const question = qna.question || "";
      const answer = qna.answer || "";
      // Generate answer if question exists and answer is empty or doesn't exist.
      if (question && question.trim() && (!answer || answer.trim() === "")) {
        // Check if this question is not already being processed.
        if (!isQuestionLoading(questionIndex)) {
          // Add the question to the list of questions to generate.
          questionsToGenerate.push({
            questionIndex,
            question: question.trim()
          });
        }
      }
    });
    if (questionsToGenerate.length === 0) return;
    // Set loading states for all questions.
    questionsToGenerate.forEach(({ questionIndex }) => {
      updateLoadingState(questionIndex, true);
    });

    // Process questions in batches of 5 to limit concurrent calls
    const batchSize = 5;
    let updatedInsights = [...newInsights];

    for (let i = 0; i < questionsToGenerate.length; i += batchSize) {
      const batch = questionsToGenerate.slice(i, i + batchSize);
      
      // Process current batch of questions concurrently
      const batchPromises = batch?.map(async ({ questionIndex, question }) => {
        try {
          const generatedAnswer = await generateTigaAnswer(question, transactionId);
          
          return {
            questionIndex,
            question,
            answer: generatedAnswer,
            success: true
          };
        } catch (error) {
          // If the answer generation fails, return an empty answer.
          return {
            questionIndex,
            question,
            answer: "",
            success: false
          };
        }
      });

      // Wait for current batch to complete
      const batchResults = await Promise.allSettled(batchPromises);

      // Process batch results and update insights
      let batchHasUpdates = false;
      batchResults?.forEach((result, batchIndex) => {
        const { questionIndex } = batch[batchIndex];
        // Clear loading state for this question
        updateLoadingState(questionIndex, false);
        if (result.status === "fulfilled" && result.value.success && result.value.answer) {
          updatedInsights[questionIndex] = {
            ...updatedInsights[questionIndex],
            answer: result.value.answer
          };
          batchHasUpdates = true;
        }
      });
      // Update insights immediately after each batch completes
      if (batchHasUpdates) {
        await updateInsights([...updatedInsights]);
      }
    }
  };

  // Handle adding a new question to the insights.
  const handleAddNewQuestion = async () => {
    // Use common function to add question and generate answer
    await addQuestionAndGenerateAnswer(newQuestion);

    setNewQuestion("");
    setIsAddingQuestion(false);
  };

  // Handle canceling the addition of a new question.
  const handleCancelAddQuestion = () => {
    setNewQuestion("");
    setIsAddingQuestion(false);
  };

  // Handle deleting a question from insights.
  const handleDeleteQuestion = async (questionIndex: number) => {
    if (!newInsights) return;
    
    const updatedInsights = [...newInsights];
    updatedInsights.splice(questionIndex, 1);
    
    // Update insights and persist
    await updateInsights(updatedInsights);
  };

  // Handle editing a question in insights.
  const handleEditQuestion = async (questionIndex: number, newQuestion: string) => {
    if (!newInsights) return;
    
    const updatedInsights = [...newInsights];
    updatedInsights[questionIndex] = {
      ...updatedInsights[questionIndex],
      question: newQuestion
    };
    
    // Update insights and persist
    await updateInsights(updatedInsights);
  };

  // Handle editing an answer in insights.
  const handleEditAnswer = async (questionIndex: number, newAnswer: string) => {
    if (!newInsights) return;
    
    const updatedInsights = [...newInsights];
    updatedInsights[questionIndex] = {
      ...updatedInsights[questionIndex],
      answer: newAnswer
    };
    await updateInsights(updatedInsights);
  };

  // Handle regenerating an answer for a question in insights.
  const handleRegenerateAnswer = async (questionIndex: number, question: string) => {
    if (!newInsights) return;
    const updatedInsights = [...newInsights];
    // Set loading state for this specific question.
    updateLoadingState(questionIndex, true);
    const generatedAnswer = await generateTigaAnswer(question, transactionId);
    if (generatedAnswer) {
      updatedInsights[questionIndex] = {
        ...updatedInsights[questionIndex],
        answer: generatedAnswer
      };
    }
    await updateInsights(updatedInsights);  
    // Clear loading state for this specific question.
    updateLoadingState(questionIndex, false);
  };

  // Check if there are any questions in the insights.
  const hasQuestions = newInsights && newInsights.length > 0 && 
    newInsights.some(qna => qna.question && qna.question.trim() !== "");

  return (
    <Box height="100%" width="100%">
      <Flex
        direction="column"
        height="100%"
        position="relative"
        overflow="hidden"
        p={2}
      >
        {/* Header */}
        <HStack
          justify="flex-end"
          p={2}
          bg="bg.surface"
          py={2}
          _hover={{ bg: "gray.50" }}
          cursor="pointer"
          flexShrink={0}
        > 
          {/* Ask New Question Button - Always available */}
          <Box onClick={(e) => e.stopPropagation()}>
            {!isAddingQuestion ? (
              <Button
                leftIcon={<Icon as={FaPlus} />}
                size={isChatOpen ? "xs" : "sm"}
                variant="outline"
                colorScheme="purple.600"
                onClick={() => setIsAddingQuestion(true)}
                fontSize={isChatOpen ? "xs" : "sm"}
                _hover={{ bg: "blue.50" }}
                isDisabled={hasAnyLoading}
              >
                Ask Question
              </Button>
            ) : (
              <HStack spacing={2}>
                <Button
                  size="xs"
                  colorScheme="blue"
                  onClick={handleAddNewQuestion}
                  isDisabled={!newQuestion.trim() || hasAnyLoading}
                >
                  Add
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={handleCancelAddQuestion}
                  isDisabled={hasAnyLoading}
                >
                  Cancel
                </Button>
              </HStack>
            )}
          </Box>
        </HStack>

        {/* Question Input - Separate row when adding */}
        { isAddingQuestion && (
          <Box p={2} borderBottom="1px solid" borderColor="gray.200" flexShrink={0}>
            <Input
              placeholder="Enter your new question..."
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              size={isChatOpen ? "sm" : "md"}
              fontSize={isChatOpen ? "xs" : "sm"}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newQuestion.trim()) {
                  handleAddNewQuestion();
                }
              }}
              autoFocus
            />
          </Box>
        )}

        <Box
          flex="1"
          overflowY="auto"
          p={2}
          css={{
            scrollbarWidth: "thin",
            scrollbarColor: "#CBD5E0 transparent",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#CBD5E0",
              borderRadius: "2px",
            },
          }}
        >
          {!hasQuestions ? (
            <Box
              textAlign="center"
              p={4}
              color="gray.500"
            >
              <Icon as={FaLightbulb} boxSize={8} mb={2} color="yellow.300" />
              <Text fontSize={isChatOpen ? "sm" : "md"} fontWeight="500">
                {newInsights?.length || 0} insight{newInsights?.length && newInsights?.length > 1 ? "s" : ""} found
              </Text>
            </Box>
          ) : (
            <Flex direction="column" height="100%">
              <VStack spacing={3} align="stretch" flex="1">
                {(newInsights || []).map((insight, index) => (
                  <InsightComponent
                    key={index}
                    insight={insight}
                    index={index}
                    isLoading={isQuestionLoading(index)}
                    onEditQuestion={handleEditQuestion}
                    onEditAnswer={handleEditAnswer}
                    onDeleteQuestion={handleDeleteQuestion}
                    onRegenerateAnswer={handleRegenerateAnswer}
                  />
                ))}
              </VStack>
            </Flex>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default InsightsPanelContainer;
